import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { getInstagramUserProfile } from '../../../hooks/instagram';
import ChatSidebar from '../pages/InstagramConversations';
import {
  InstagramInboxShell,
  InstagramThreadPlaceholder,
  igAccent,
  igAccentSoft,
} from './instagramInboxUi';

const InstagramLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccount, setHasAccount] = useState(false);
  const hasChildren = React.Children.count(children) > 0;

  useEffect(() => {
    const loadAccount = async () => {
      setLoading(true);
      try {
        const response = await getInstagramUserProfile(orgId);
        setHasAccount(Boolean(response.success && Array.isArray(response.data) && response.data.length));
      } finally {
        setLoading(false);
      }
    };

    loadAccount();
  }, [orgId]);

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="#F7F4F8">
        <Spinner color={igAccent} size="xl" />
      </Flex>
    );
  }

  if (!hasAccount || !orgId) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="#F7F4F8" px={6}>
        <Box
          maxW="520px"
          w="full"
          bg="white"
          borderRadius="28px"
          border="1px solid"
          borderColor="rgba(15, 23, 42, 0.08)"
          boxShadow="0 24px 64px rgba(15,23,42,0.08)"
          p={{ base: 8, md: 10 }}
        >
          <VStack spacing={4} textAlign="center">
            <Box
              boxSize="72px"
              borderRadius="24px"
              bg={igAccentSoft}
              color={igAccent}
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="30px"
            >
              @
            </Box>
            <VStack spacing={1}>
              <Text fontWeight="800" fontSize="2xl" color="gray.900">
                Connect Instagram first
              </Text>
              <Text color="gray.500" maxW="360px" lineHeight="1.7">
                Link your Instagram business account to unlock DMs, comments,
                and inbox tools inside one workspace.
              </Text>
            </VStack>
            <Button
              colorScheme="pink"
              borderRadius="full"
              px={6}
              onClick={() => navigate('/organization/inbox/instagram')}
            >
              Connect Instagram
            </Button>
          </VStack>
        </Box>
      </Flex>
    );
  }

  return (
    <InstagramInboxShell
      sidebar={<ChatSidebar orgId={orgId} />}
      thread={hasChildren ? children : <InstagramThreadPlaceholder />}
    />
  );
};

export default InstagramLayout;
